/**
 * LLM BOM Processor
 * 
 * Uses LLMs to intelligently parse and structure BOM data from raw text.
 * Supports multiple providers:
 * - OpenAI GPT-4 (paid)
 * - Groq Llama 3.1 (FREE!)
 */

// Store API keys in localStorage
const OPENAI_KEY_STORAGE = 'openai_api_key';
const GROQ_KEY_STORAGE = 'groq_api_key';
const LLM_PROVIDER_STORAGE = 'llm_provider';

// Available LLM providers
export const LLM_PROVIDERS = {
  GROQ: {
    id: 'groq',
    name: 'Groq (Llama 3.3)',
    description: 'FREE! Fast inference with Llama 3.3 70B',
    apiUrl: 'https://api.groq.com/openai/v1/chat/completions',
    model: 'llama-3.3-70b-versatile',
    keyUrl: 'https://console.groq.com/keys',
    free: true
  },
  OPENAI: {
    id: 'openai',
    name: 'OpenAI GPT-4',
    description: 'Most powerful, requires paid account',
    apiUrl: 'https://api.openai.com/v1/chat/completions',
    model: 'gpt-4o',
    keyUrl: 'https://platform.openai.com/api-keys',
    free: false
  }
};

/**
 * Get current LLM provider
 */
export function getLlmProvider() {
  return localStorage.getItem(LLM_PROVIDER_STORAGE) || 'groq'; // Default to free Groq
}

/**
 * Set LLM provider
 */
export function setLlmProvider(provider) {
  localStorage.setItem(LLM_PROVIDER_STORAGE, provider);
}

/**
 * Check if LLM is configured (any provider)
 */
export function isLlmConfigured() {
  const provider = getLlmProvider();
  return !!getApiKey(provider);
}

/**
 * Get stored API key for a provider
 */
export function getApiKey(provider = null) {
  const p = provider || getLlmProvider();
  if (p === 'openai') {
    return localStorage.getItem(OPENAI_KEY_STORAGE);
  }
  return localStorage.getItem(GROQ_KEY_STORAGE);
}

/**
 * Set API key for a provider
 */
export function setApiKey(key, provider = null) {
  const p = provider || getLlmProvider();
  const storage = p === 'openai' ? OPENAI_KEY_STORAGE : GROQ_KEY_STORAGE;
  
  if (key) {
    localStorage.setItem(storage, key);
  } else {
    localStorage.removeItem(storage);
  }
}

/**
 * Clear stored API key for current provider
 */
export function clearApiKey(provider = null) {
  const p = provider || getLlmProvider();
  if (p === 'openai') {
    localStorage.removeItem(OPENAI_KEY_STORAGE);
  } else {
    localStorage.removeItem(GROQ_KEY_STORAGE);
  }
}

/**
 * Process raw text with LLM to extract structured BOM data
 * @param {string} rawText - Raw text from OCR or PDF extraction
 * @param {Object} options - Processing options
 * @returns {Promise<Object>} - Structured BOM data
 */
export async function processWithLlm(rawText, options = {}) {
  const provider = getLlmProvider();
  const providerConfig = LLM_PROVIDERS[provider.toUpperCase()] || LLM_PROVIDERS.GROQ;
  const apiKey = getApiKey(provider);
  
  if (!apiKey) {
    return {
      success: false,
      error: `API key not configured for ${providerConfig.name}. Please set your API key.`,
      items: []
    };
  }
  
  const { 
    expectedColumns = null,
    context = 'piping isometric BOM'
  } = options;
  
  const systemPrompt = buildSystemPrompt(expectedColumns, context);
  const userPrompt = buildUserPrompt(rawText);
  
  try {
    const requestBody = {
      model: providerConfig.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: 0.1,
      max_tokens: 4000
    };
    
    // Only OpenAI supports response_format
    if (provider === 'openai') {
      requestBody.response_format = { type: 'json_object' };
    }
    
    const response = await fetch(providerConfig.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `API error: ${response.status}`);
    }
    
    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error(`No response from ${providerConfig.name}`);
    }
    
    // Parse the JSON response (extract JSON from response if needed)
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch {
      // Try to extract JSON from markdown code blocks
      const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        parsed = JSON.parse(jsonMatch[1]);
      } else {
        // Try to find JSON object in the response
        const objMatch = content.match(/\{[\s\S]*\}/);
        if (objMatch) {
          parsed = JSON.parse(objMatch[0]);
        } else {
          throw new Error('Could not parse LLM response as JSON');
        }
      }
    }
    
    return {
      success: true,
      items: parsed.items || [],
      headers: parsed.headers || [],
      confidence: parsed.confidence || 90,
      notes: parsed.notes || '',
      provider: providerConfig.name,
      rawResponse: content
    };
    
  } catch (error) {
    console.error('LLM processing failed:', error);
    return {
      success: false,
      error: error.message,
      items: []
    };
  }
}

/**
 * Build system prompt for BOM extraction
 */
function buildSystemPrompt(expectedColumns, context) {
  const defaultColumns = [
    'iso_sheet_number',
    'type',
    'material', 
    'schedule',
    'size',
    'item_code',
    'quantity',
    'description'
  ];
  
  const columns = expectedColumns || defaultColumns;
  
  return `You are an expert at extracting Bill of Materials (BOM) data from ${context} documents.

Your task is to parse raw text (from OCR or PDF extraction) and extract structured table data.

EXPECTED COLUMNS (use these exact field names):
${columns.map((col, i) => `${i + 1}. ${col}`).join('\n')}

RULES:
1. Extract ALL rows from the table, even if some cells are empty
2. Preserve exact values - don't modify part numbers, codes, or specifications
3. Handle multi-line descriptions by combining them
4. If a column doesn't exist in the source, omit it from the output
5. Recognize common piping abbreviations:
   - SS = Stainless Steel
   - CS = Carbon Steel
   - SCH or S- = Schedule (e.g., S-10S, SCH 40)
   - BE = Bevel End
   - SMLS = Seamless
   - Deg. = Degree (for elbows)

RESPOND WITH JSON:
{
  "headers": ["column1", "column2", ...],
  "items": [
    {"iso_sheet_number": "...", "type": "...", "quantity": 5, ...},
    ...
  ],
  "confidence": 85,
  "notes": "Any issues or observations"
}

Be thorough - extract every row you can identify.`;
}

/**
 * Build user prompt with the raw text
 */
function buildUserPrompt(rawText) {
  return `Please extract the BOM table data from this text:

---
${rawText}
---

Return the structured data as JSON with all items found.`;
}

/**
 * Validate API key by making a test request
 */
export async function validateApiKey(key, provider = null) {
  const p = provider || getLlmProvider();
  
  try {
    if (p === 'openai') {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: { 'Authorization': `Bearer ${key}` }
      });
      return response.ok;
    } else {
      // Groq validation
      const response = await fetch('https://api.groq.com/openai/v1/models', {
        headers: { 'Authorization': `Bearer ${key}` }
      });
      return response.ok;
    }
  } catch {
    return false;
  }
}

/**
 * Get estimated cost for processing
 * @param {string} text - Text to process
 * @returns {Object} - Estimated tokens and cost
 */
export function estimateCost(text) {
  // Rough estimation: ~4 characters per token
  const inputTokens = Math.ceil(text.length / 4);
  const outputTokens = Math.ceil(inputTokens * 0.5); // Estimate output is half of input
  
  // GPT-4o pricing (as of 2024): $5/1M input, $15/1M output
  const inputCost = (inputTokens / 1000000) * 5;
  const outputCost = (outputTokens / 1000000) * 15;
  
  return {
    inputTokens,
    outputTokens,
    estimatedCost: `$${(inputCost + outputCost).toFixed(4)}`,
    note: 'Actual cost may vary'
  };
}

const llmBomProcessor = {
  LLM_PROVIDERS,
  getLlmProvider,
  setLlmProvider,
  isLlmConfigured,
  getApiKey,
  setApiKey,
  clearApiKey,
  processWithLlm,
  validateApiKey,
  estimateCost
};

export default llmBomProcessor;
